/**
 * Access to the deferred install prompt, and the app-wide events that publish it.
 *
 * `beforeinstallprompt` can fire *before the bundle runs*, and it cannot be replayed afterwards:
 * Chromium dispatches it as soon as it has fetched the manifest and concluded the app is
 * installable. A listener added by this module, loaded from a deferred `type="module"` script,
 * may well be added too late. So the capture is not here: it is the inline classic script in the
 * `<head>` of `public/index.html`, which runs during parsing. It calls `preventDefault()`
 * (suppressing Chromium's own mini-infobar) and parks the event on `window`. This module is the
 * typed way to read that parking spot.
 *
 * The two event names below are duplicated as literals in that inline script, which runs before
 * any module and cannot import them: change one, change the other. They are dispatched on
 * `document`, not `document.body` as the other app-wide events: the script runs in the `<head>`,
 * where there is no body yet.
 */

/** A deferred prompt is parked and ready to be shown. */
export const INSTALL_AVAILABLE_EVENT = 'install/available';

/** The app was installed: by our prompt or by the browser's own menu. */
export const INSTALL_COMPLETED_EVENT = 'install/completed';

// Written by the inline script in index.html, read only through this module.
const BUFFER_KEY = '__planeDeferredInstallPrompt';

/**
 * Returns the parked `beforeinstallprompt` event, or `null` if there is none.
 *
 * The event is single-use: once `prompt()` has been called on it, calling it again throws. So the
 * default is to *take* it, clearing the buffer, so a second caller gets `null` rather than a dead
 * event. Pass `{ peek: true }` to ask whether one exists without claiming it.
 *
 * @param {{peek?: boolean}} [options]
 * @returns {BeforeInstallPromptEvent|null}
 */
export function takeInstallPrompt({ peek = false } = {}) {
  const deferredPrompt = window[BUFFER_KEY] ?? null;
  if (deferredPrompt && !peek) {
    window[BUFFER_KEY] = null;
  }
  return deferredPrompt;
}
